import React, { useState } from "react";
import { toast } from "sonner";
import { Copy, Mail, Phone, ExternalLink, Check, Pencil, Trash2, Plus, Save, X as XIcon } from "lucide-react";

const copyText = async (text, label) => {
  try { await navigator.clipboard.writeText(text); toast.success(`${label} copied`); } catch { toast.error("Copy failed"); }
};

export const LEVEL_COLORS = { "High Major": "#f0b429", "Mid Major +": "#3d9cff", "Mid Major": "#34d399", "Low Major": "#a0a0a0", "High Academic": "#c084fc" };
export const DIVISION_SHORT = { "NCAA D1": "D1", "NCAA D2": "D2", "NCAA D3": "D3", NAIA: "NAIA", JUCO: "JUCO" };

export const ratingLabel = (source) => (source === "d3datacast" ? "D3" : "KenPom");

export function SportTag({ sport, className = "" }) {
  if (sport !== "womens") return null;
  return <span className={`inline-flex items-center h-5 px-1.5 border border-pink-400/50 text-pink-300 text-[9px] uppercase tracking-[0.2em] font-bold ${className}`} data-testid="sport-tag-womens">WBB</span>;
}

export function DivisionTag({ division, className = "" }) {
  if (!division || division === "NCAA D1") return null;
  return <span className={`inline-flex items-center h-5 px-1.5 border border-white/20 text-white/80 text-[9px] uppercase tracking-[0.2em] font-bold ${className}`} data-testid={`division-tag-${(DIVISION_SHORT[division] || division).toLowerCase()}`}>{DIVISION_SHORT[division] || division}</span>;
}

export function LevelPill({ level, className = "" }) {
  if (!level) return null;
  const c = LEVEL_COLORS[level] || "#a0a0a0";
  return (
    <span className={`inline-flex items-center h-5 px-1.5 border text-[9px] uppercase tracking-[0.2em] font-bold whitespace-nowrap ${className}`}
      style={{ color: c, borderColor: `${c}66`, background: `${c}14` }} data-testid={`level-pill-${level.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      {level}
    </span>
  );
}

const fmt1 = (v) => (v === null || v === undefined || v === "" ? "—" : Number(v).toFixed(1));
const signed = (v) => (v === null || v === undefined || v === "" ? "—" : `${Number(v) > 0 ? "+" : ""}${Number(v).toFixed(2)}`);

// KenPom line: Rank · Record · ORtg · DRtg · Net
export function KenPomStrip({ kenpom, compact = false }) {
  if (!kenpom || kenpom.rank == null) return null;
  const cells = [
    [kenpom.source === "d3datacast" ? "D3 Datacast" : "KenPom", `#${kenpom.rank}`], ["Record", `${kenpom.wins ?? "—"}-${kenpom.losses ?? "—"}`],
    ["ORtg", `${fmt1(kenpom.adj_oe)}`, kenpom.rank_adj_oe], ["DRtg", `${fmt1(kenpom.adj_de)}`, kenpom.rank_adj_de], ["Net", signed(kenpom.adj_em)],
  ];
  return (
    <div className="inline-flex flex-wrap items-stretch gap-px bg-white/10" data-testid="kenpom-strip">
      {cells.map(([k, v, rk]) => (
        <div key={k} className={`bg-[#111] ${compact ? "px-2.5 py-1.5" : "px-3 py-2"} min-w-[84px]`}>
          <div className="text-[9px] uppercase tracking-[0.25em] text-[#757575]">{k}</div>
          <div className={`font-heading ${compact ? "text-sm" : "text-base"} text-white mt-0.5 flex items-baseline gap-1`}>{v}{rk ? <span className="text-[9px] text-[#757575] font-body">#{rk}</span> : null}</div>
        </div>
      ))}
      <div className="bg-[#111] px-3 py-2 flex items-end text-[9px] text-[#555] normal-case">{kenpom.season ? `${kenpom.season - 1}-${String(kenpom.season).slice(2)}` : ""}{kenpom.data_through ? ` · thru ${kenpom.data_through}` : ""}</div>
    </div>
  );
}

export function SchoolLogo({ program, size = 40, className = "" }) {
  const [broken, setBroken] = useState(false);
  const initials = (program.short_name || program.school || "?").split(/\s+/).slice(0, 2).map(w => w[0]).join("").toUpperCase();
  if (!program.logo_url || broken) {
    return <div className={`shrink-0 flex items-center justify-center bg-white/[0.06] border border-white/10 font-heading text-[#a0a0a0] ${className}`} style={{ width: size, height: size, fontSize: size * 0.38 }} data-testid="school-logo-fallback">{initials}</div>;
  }
  return <img src={program.logo_url} alt="" onError={() => setBroken(true)} className={`shrink-0 object-contain bg-white/[0.04] p-1 ${className}`} style={{ width: size, height: size }} data-testid="school-logo" />;
}

export function StaffAvatar({ person, size = 40 }) {
  const [broken, setBroken] = useState(false);
  const initials = (person.name || "?").split(/\s+/).slice(0, 2).map(w => w[0]).join("").toUpperCase();
  if (!person.photo_url || broken) {
    return <div className="shrink-0 rounded-full flex items-center justify-center bg-white/[0.06] border border-white/10 font-heading text-[#a0a0a0]" style={{ width: size, height: size, fontSize: size * 0.36 }} data-testid="staff-avatar-fallback">{initials}</div>;
  }
  return <img src={person.photo_url} alt="" loading="lazy" onError={() => setBroken(true)} className="shrink-0 rounded-full object-cover object-top border border-white/10" style={{ width: size, height: size }} data-testid="staff-avatar" />;
}

export function AcademicSnapshot({ academics = {}, program = {} }) {
  const items = [
    ["GPA", academics.gpa], ["SAT Read", academics.sat_reading], ["SAT Math", academics.sat_math], ["ACT", academics.act],
    ["Accept.", academics.acceptance_rate], ["Yearly Cost", academics.yearly_cost], ["Undergrads", academics.undergrads],
    ["Type", academics.private_public && academics.private_public.replace(" (not-for-profit)", "")],
  ].filter(([, v]) => v);
  if (!items.length) return null;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/10" data-testid="academic-snapshot">
      {items.map(([k, v]) => (
        <div key={k} className="bg-[#111] px-3 py-2">
          <div className="text-[9px] uppercase tracking-[0.25em] text-[#757575]">{k}</div>
          <div className="font-heading text-base text-white mt-0.5 truncate" title={v}>{v}</div>
        </div>
      ))}
    </div>
  );
}

// Staff table with copy/email actions + optional inline manual editing (admins).
export function StaffTable({ staff = [], editable = false, onSave, compact = false }) {
  const [editing, setEditing] = useState(false);
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);

  const start = () => { setRows(staff.map(s => ({ ...s }))); setEditing(true); };
  const save = async () => {
    setSaving(true);
    try { await onSave(rows.map(r => ({ ...r, manual: true }))); setEditing(false); toast.success("Staff saved"); }
    catch { toast.error("Save failed"); } finally { setSaving(false); }
  };
  const setRow = (i, k, v) => setRows(rs => rs.map((r, j) => j === i ? { ...r, [k]: v } : r));

  if (editing) {
    return (
      <div className="space-y-2" data-testid="staff-editor">
        {rows.map((r, i) => (
          <div key={i} className="grid grid-cols-12 gap-2">
            <input value={r.name || ""} onChange={e => setRow(i, "name", e.target.value)} placeholder="Name" className="col-span-3 h-9 px-2 bg-[#0e0e0e] border border-white/15 text-sm text-white focus:outline-none" data-testid={`staff-edit-name-${i}`} />
            <input value={r.title || ""} onChange={e => setRow(i, "title", e.target.value)} placeholder="Title" className="col-span-3 h-9 px-2 bg-[#0e0e0e] border border-white/15 text-sm text-white focus:outline-none" data-testid={`staff-edit-title-${i}`} />
            <input value={r.email || ""} onChange={e => setRow(i, "email", e.target.value)} placeholder="Email" className="col-span-3 h-9 px-2 bg-[#0e0e0e] border border-white/15 text-sm text-white focus:outline-none" data-testid={`staff-edit-email-${i}`} />
            <input value={r.phone || ""} onChange={e => setRow(i, "phone", e.target.value)} placeholder="Phone" className="col-span-2 h-9 px-2 bg-[#0e0e0e] border border-white/15 text-sm text-white focus:outline-none" data-testid={`staff-edit-phone-${i}`} />
            <button onClick={() => setRows(rs => rs.filter((_, j) => j !== i))} className="col-span-1 h-9 border border-white/10 text-[#757575] hover:text-red-300 flex items-center justify-center" data-testid={`staff-edit-remove-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
        <div className="flex items-center gap-2 pt-1">
          <button onClick={() => setRows(rs => [...rs, { name: "", title: "", email: "", phone: "" }])} className="h-9 px-3 border border-white/15 text-[10px] uppercase tracking-widest text-[#a0a0a0] hover:text-white flex items-center gap-1.5" data-testid="staff-edit-add"><Plus className="w-3 h-3" /> Add person</button>
          <button onClick={save} disabled={saving} className="h-9 px-4 bg-[var(--org-accent)] text-[#121212] text-[10px] uppercase tracking-widest font-bold flex items-center gap-1.5 disabled:opacity-50" data-testid="staff-edit-save"><Save className="w-3 h-3" /> Save</button>
          <button onClick={() => setEditing(false)} className="h-9 px-3 text-[10px] uppercase tracking-widest text-[#757575] hover:text-white flex items-center gap-1.5" data-testid="staff-edit-cancel"><XIcon className="w-3 h-3" /> Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="staff-table">
      {!staff.length && <div className="text-sm text-[#757575] italic py-2">No staff on file{editable ? " — add them manually below." : "."}</div>}
      <div className="divide-y divide-white/[0.06]">
        {staff.map((s, i) => (
          <div key={`${s.name}-${i}`} className={`flex flex-wrap items-center gap-x-4 gap-y-1 ${compact ? "py-2" : "py-2.5"}`} data-testid={`staff-row-${i}`}>
            <StaffAvatar person={s} size={compact ? 34 : 44} />
            <div className="min-w-[180px] flex-1">
              <div className="text-sm font-semibold text-white flex items-center gap-2">
                {s.profile_url ? <a href={s.profile_url} target="_blank" rel="noreferrer" className="hover:text-[var(--org-accent)] inline-flex items-center gap-1">{s.name}<ExternalLink className="w-3 h-3 opacity-50" /></a> : s.name}
                {s.manual && <span className="text-[8px] uppercase tracking-widest px-1 border border-white/15 text-[#757575]">manual</span>}
              </div>
              <div className="text-[11px] text-[#a0a0a0]">{s.title || "—"}</div>
            </div>
            <div className="flex items-center gap-1.5 text-xs">
              {s.email ? (
                <>
                  <a href={`mailto:${s.email}`} className="inline-flex items-center gap-1.5 h-7 px-2 border border-white/10 text-[#c0c0c0] hover:text-white hover:border-[var(--org-accent)]" style={{ transition: "border-color .15s ease, color .15s ease" }} data-testid={`staff-email-${i}`}><Mail className="w-3 h-3 text-[var(--org-accent)]" />{s.email}</a>
                  <button onClick={() => copyText(s.email, "Email")} className="h-7 w-7 border border-white/10 text-[#757575] hover:text-white flex items-center justify-center" title="Copy email" data-testid={`staff-copy-email-${i}`}><Copy className="w-3 h-3" /></button>
                </>
              ) : <span className="h-7 px-2 inline-flex items-center text-[#555] border border-dashed border-white/10">no email listed</span>}
              {s.phone && (
                <>
                  <a href={`tel:${s.phone}`} className="inline-flex items-center gap-1.5 h-7 px-2 border border-white/10 text-[#c0c0c0] hover:text-white" data-testid={`staff-phone-${i}`}><Phone className="w-3 h-3 text-[#757575]" />{s.phone}</a>
                  <button onClick={() => copyText(s.phone, "Phone")} className="h-7 w-7 border border-white/10 text-[#757575] hover:text-white flex items-center justify-center" title="Copy phone" data-testid={`staff-copy-phone-${i}`}><Copy className="w-3 h-3" /></button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
      {editable && (
        <div className="flex items-center gap-2 pt-2">
          <button onClick={start} className="h-8 px-3 border border-white/15 text-[10px] uppercase tracking-widest text-[#a0a0a0] hover:text-white flex items-center gap-1.5" data-testid="staff-edit-start"><Pencil className="w-3 h-3" /> Edit staff</button>
          {staff.some(s => s.email) && (
            <button onClick={() => copyText(staff.filter(s => s.email).map(s => s.email).join(", "), "All emails")} className="h-8 px-3 border border-white/15 text-[10px] uppercase tracking-widest text-[#a0a0a0] hover:text-white flex items-center gap-1.5" data-testid="staff-copy-all"><Check className="w-3 h-3" /> Copy all emails</button>
          )}
        </div>
      )}
    </div>
  );
}
